import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LoginModal from "@/components/LoginModal";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Video, ShieldCheck, Activity, Stethoscope, Brain, Building2,
  ArrowUpRight, Play, FileText, Pill, HeartPulse, Lock, BadgeCheck, Wifi, ArrowRight,
  Heart, FolderLock, CalendarClock, Users
} from "lucide-react";

const IMG = {
  family: "https://images.pexels.com/photos/4307880/pexels-photo-4307880.jpeg?auto=compress&cs=tinysrgb&w=900",
  vitals: "https://images.pexels.com/photos/19438562/pexels-photo-19438562.jpeg?auto=compress&cs=tinysrgb&w=900",
  careRoom: "https://images.pexels.com/photos/19438563/pexels-photo-19438563.jpeg?auto=compress&cs=tinysrgb&w=1000",
  hospital: "https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGJ1aWxkaW5nJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NDgyNjcwM3ww&ixlib=rb-4.1.0&q=85&w=900",
};

const easeOut = [0.16, 1, 0.3, 1];

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: easeOut, delay }}>{children}</motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: easeOut, delay }}>
    {children}
  </motion.div>
);

const SERVICES = [
  { n: "01", icon: Stethoscope, title: "24/7 Virtual Acute Care", desc: "Connect via phone or HD video with verified MBBS/MD doctors within minutes — anytime, any day." },
  { n: "02", icon: HeartPulse, title: "Primary Care & Longitudinal Health", desc: "Continuous care with a dedicated physician and care team, including a welcome health kit mailed home." },
  { n: "03", icon: Activity, title: "Connected Chronic Care", desc: "Real-time remote monitoring for diabetes, hypertension & weight using cellular-connected BP cuffs, scales & glucose meters." },
  { n: "04", icon: BadgeCheck, title: "Expert Medical Opinions", desc: "Access top global & national specialists for second opinions on complex diagnoses, cancer care or surgery decisions." },
  { n: "05", icon: Brain, title: "Mental Health & Therapy", desc: "Confidential video and chat sessions with licensed psychologists and therapists." },
  { n: "06", icon: Building2, title: "Hospital & Health System Solutions", desc: "Enterprise software enabling telestroke, virtual ICU (tele-ICU) and virtual nursing to expand bed capacity." },
];

export default function Landing() {
  const [loginOpen, setLoginOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const familyY = useTransform(scrollYProgress, [0, 0.3], [0, -40]);
  const vitalsY = useTransform(scrollYProgress, [0, 0.3], [0, 40]);

  const goConsult = () => user ? navigate("/dashboard") : setLoginOpen(true);

  return (
    <div className="bg-[#FAFAFA] overflow-x-hidden">
      <Navbar onOpenLogin={() => setLoginOpen(true)} />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => navigate("/dashboard")} />

      {/* HERO */}
      <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 px-6 lg:px-10 overflow-hidden">
        {/* Warm marigold radial glow */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-24 left-1/3 -translate-x-1/2 w-[800px] h-[800px] rounded-full blur-3xl"
               style={{ background: "radial-gradient(circle, rgba(254,243,199,0.65) 0%, rgba(254,243,199,0.25) 45%, rgba(255,255,255,0) 75%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 text-[#92400E] text-xs font-bold uppercase tracking-[0.15em] mb-7">
              <span className="flame-dot w-2 h-2 rounded-full bg-[#F59E0B]" /> परिवार जैसा प्यार · Care that feels like family
            </motion.div>
            <h1 className="font-head font-light text-[#041E26] text-5xl md:text-6xl lg:text-[4.4rem] leading-[1.02] tracking-tighter">
              <HeroLine delay={0.1}>Comprehensive Care,</HeroLine>
              <HeroLine delay={0.22}><span className="text-[#D97706] font-normal">Illuminated.</span></HeroLine>
              <HeroLine delay={0.34}>From Hospital to Home.</HeroLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-6 flex items-center gap-3 text-[#B45309]">
              <span className="h-px w-8 bg-[#D97706]/60" />
              <span className="font-serif-logo italic text-lg tracking-wide">"स्वस्थ परिवार, सुखी परिवार"</span>
              <span className="h-px w-8 bg-[#D97706]/60" />
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.7 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              A doctor for a midnight fever. A therapist who listens. A nurse who visits your parents at home. Candle brings caring, dependable healthcare to your whole <span className="italic text-[#7A1E38] font-medium">parivaar</span> — in your language, across every corner of India.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
              className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-book-consult-btn" onClick={goConsult}
                className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold h-14 px-8 text-base group shadow-md">
                <Video className="w-5 h-5 mr-1" /> Book a Video Appointment
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button data-testid="hero-b2b-btn" onClick={() => setLoginOpen(true)} variant="outline"
                className="rounded-full border-[#7A1E38]/30 text-[#7A1E38] font-semibold h-14 px-8 text-base hover:bg-[#7A1E38] hover:text-white hover:border-[#7A1E38]">
                Partner with Us (B2B)
              </Button>
            </motion.div>
            <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#0A2F3C]" /> ABDM Integrated</span>
              <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#0A2F3C]" /> E2E Encrypted</span>
              <span className="flex items-center gap-1.5 text-[#B45309] font-medium">हिंदी · English · தமிழ் · বাংলা</span>
            </div>
          </div>

          {/* Split parallax visual */}
          <div className="lg:col-span-6 relative h-[420px] sm:h-[500px]">
            <motion.div style={{ y: familyY }} className="absolute left-0 top-4 w-[62%] z-10">
              <div className="rounded-[1.75rem] overflow-hidden shadow-[0_20px_60px_rgba(4,30,38,0.22)] border-4 border-white">
                <img src={IMG.family} alt="Indian family on a video consultation" className="w-full h-[300px] object-cover" />
              </div>
              <div className="mt-3 bg-white rounded-2xl p-3.5 shadow-lg flex items-center gap-3 w-max">
                <span className="flame-dot w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="text-xs font-semibold text-[#041E26]">Appointment confirmed <span className="text-slate-400">· today 4:30 PM</span></span>
              </div>
            </motion.div>
            <motion.div style={{ y: vitalsY }} className="absolute right-0 bottom-0 w-[58%] z-20">
              <div className="rounded-[1.75rem] overflow-hidden shadow-[0_20px_60px_rgba(4,30,38,0.22)] border-4 border-white">
                <img src={IMG.vitals} alt="Indian doctor reviewing patient vitals" className="w-full h-[280px] object-cover" />
              </div>
              <div className="mt-3 ml-auto bg-[#041E26] rounded-2xl p-3.5 shadow-lg flex items-center gap-3 w-max">
                <Activity className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-xs font-semibold text-white">Live vitals synced</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-amber-100/70 py-4 border-y border-amber-200 overflow-hidden" aria-hidden="true">
        <div className="animate-marquee inline-flex items-center gap-8 text-[#041E26] font-head font-medium text-lg">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="inline-flex items-center gap-8">
              {["24/7 Care", "ABDM Compliant", "Eldercare at Home", "Care in your language", "Expert Opinions", "Secure & Private", "Tele-ICU", "Verified Doctors"].map((t) => (
                <span key={t} className="inline-flex items-center gap-8"><span>{t}</span><span className="text-[#F59E0B]">✦</span></span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* VIRTUAL CARE ROOM — dark section */}
      <section id="features" className="bg-[#041E26] py-24 lg:py-32 px-6 lg:px-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#F59E0B]/10 blur-[120px]" aria-hidden="true" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">वर्चुअल केयर कक्ष · The Virtual Care Room</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-white mt-4 tracking-tight leading-tight">
              Clinic-grade video, <br /> optimised for every corner of <span className="italic text-[#F5D08B]">Bharat</span>.
            </h2>
            <p className="text-white/60 mt-5 text-lg leading-relaxed">Low-latency HD consultations that hold up across Tier 1, 2 and 3 network conditions — because good doctors shouldn't need good Wi-Fi.</p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Wifi, t: "Low-latency HD video", d: "Adaptive bitrate keeps calls stable on 3G, 4G & Wi-Fi." },
                { icon: FileText, t: "Secure in-call sharing", d: "Share documents & lab reports mid-consultation." },
                { icon: Pill, t: "Instant e-prescriptions", d: "Digital scripts sent to your local pharmacy instantly." },
                { icon: ShieldCheck, t: "ABDM-compliant records", d: "Every visit linked to your Ayushman Bharat health ID." },
              ].map((f, i) => (
                <Reveal key={f.t} delay={i * 0.08}>
                  <div className="tracing-border rounded-2xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5 text-[#F59E0B]" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">{f.t}</h4>
                      <p className="text-white/50 text-sm mt-0.5">{f.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={IMG.careRoom} alt="Virtual Care Room preview" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#041E26] via-transparent to-transparent" />
              <button onClick={goConsult} data-testid="care-room-play-btn"
                className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-[#F59E0B] flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.5)] hover:scale-105 transition-transform">
                <Play className="w-8 h-8 text-[#041E26] ml-1" fill="#041E26" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES — numbered manifesto */}
      <section id="services" className="py-24 lg:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">सेवा की परतें · Care, layered</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] mt-4 tracking-tight leading-tight">
              A complete health system, from acute visits to enterprise <span className="italic text-[#7A1E38]">ICU</span>.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) * 0.08}>
                <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }}
                  className="group bg-white rounded-2xl border border-slate-200 p-7 h-full hover:shadow-[0_20px_50px_rgba(4,30,38,0.1)] transition-shadow">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-head text-5xl font-extralight text-slate-200 group-hover:text-[#F59E0B] transition-colors">{s.n}</span>
                    <div className="w-12 h-12 rounded-xl bg-[#041E26] flex items-center justify-center">
                      <s.icon className="w-6 h-6 text-[#F59E0B]" />
                    </div>
                  </div>
                  <h3 className="font-head text-xl font-semibold text-[#041E26] leading-snug">{s.title}</h3>
                  <p className="text-slate-500 mt-3 text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F3F4F6] py-24 lg:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] tracking-tight">How a video consultation works</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "1", t: "Choose & Book", d: "Log in via Phone/Email OTP, pick a provider by care category and book an available time slot." },
              { n: "2", t: "Request to Join", d: "At your scheduled time, request to join — your provider reviews and admits you into the encrypted room." },
              { n: "3", t: "Care that follows you", d: "Receive digital prescriptions, home lab orders and care-team follow-up." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="relative">
                  <div className="font-head text-8xl font-extralight text-[#F59E0B]/30">{s.n}</div>
                  <h3 className="font-head text-2xl font-semibold text-[#041E26] mt-2">{s.t}</h3>
                  <p className="text-slate-600 mt-3 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENTLY ADDED — new ways we care */}
      <section className="py-24 lg:py-28 px-6 lg:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-4">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">New on Candle</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] mt-4 tracking-tight leading-tight">More ways we look after your family.</h2>
            <p className="text-slate-600 mt-4 text-lg">We&apos;ve been busy adding the things Indian families actually ask us for — from caring for parents at home to keeping every prescription safe in one place.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { icon: Heart, tag: "Maa Papa Care", title: "Eldercare at home", desc: "Warm Sewa Pals for companionship plus nurses, doctors, home diagnostics and 24/7 SOS — perfect for NRIs caring for parents from afar.", cta: "Explore eldercare", act: () => navigate("/eldercare"), dark: true },
              { icon: FolderLock, tag: "Health records", title: "My Medical Records", desc: "Keep prescriptions and lab reports safe in one private vault — and share them securely with your doctor before every visit.", cta: "Sign in to upload", act: () => setLoginOpen(true) },
              { icon: Brain, tag: "Mann ki baat", title: "Therapists who listen", desc: "Confidential video and chat sessions with licensed psychologists — because mental health deserves the same care as physical health.", cta: "Talk to someone", act: () => setLoginOpen(true) },
              { icon: CalendarClock, tag: "Appointments", title: "Book a time that suits you", desc: "Pick your doctor, choose a slot that fits around work and family, pay easily by UPI, and join the video call when it's time.", cta: "Book now", act: goConsult },
            ].map((f, i) => (
              <Reveal key={f.title} delay={(i % 4) * 0.08}>
                <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }}
                  className={`rounded-2xl p-7 h-full flex flex-col ${f.dark ? "bg-[#041E26] text-white" : "bg-[#FAFAFA] border border-slate-200"}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${f.dark ? "bg-[#F59E0B]/20" : "bg-amber-100"}`}>
                    <f.icon className={`w-6 h-6 ${f.dark ? "text-[#F59E0B]" : "text-[#D97706]"}`} />
                  </div>
                  <span className={`mt-5 text-[10px] uppercase tracking-[0.15em] font-bold ${f.dark ? "text-[#F59E0B]" : "text-[#D97706]"}`}>{f.tag}</span>
                  <h3 className={`font-head text-xl font-semibold mt-1 ${f.dark ? "text-white" : "text-[#041E26]"}`}>{f.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed flex-1 ${f.dark ? "text-white/60" : "text-slate-500"}`}>{f.desc}</p>
                  <button data-testid={`new-feature-${i}`} onClick={f.act} className={`mt-5 inline-flex items-center gap-1 text-sm font-semibold ${f.dark ? "text-[#F59E0B]" : "text-[#D97706]"} hover:gap-2 transition-all`}>
                    {f.cta} <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* TRUST BANNER */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Trusted & Compliant</span>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, t: "ABDM / Ayushman Bharat", d: "Digital Mission Integrated" },
              { icon: Lock, t: "HIPAA & Telemedicine", d: "100% Guidelines Compliant" },
              { icon: Video, t: "End-to-End Encrypted", d: "Secure Video Calls" },
              { icon: BadgeCheck, t: "Verified Clinicians", d: "Medical Council Registered" },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center h-full">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <b.icon className="w-6 h-6 text-[#D97706]" />
                  </div>
                  <h4 className="font-semibold text-[#041E26] text-sm">{b.t}</h4>
                  <p className="text-slate-400 text-xs mt-1">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE CTA */}
      <section className="px-6 lg:px-10 pb-24">
        <div className="max-w-7xl mx-auto rounded-[2rem] overflow-hidden relative bg-[#041E26]">
          <img src={IMG.hospital} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="relative px-8 lg:px-16 py-16 lg:py-20 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">For Hospitals & Health Systems</span>
              <h2 className="font-head text-3xl md:text-4xl font-light text-white mt-4 leading-tight">Expand capacity with tele-ICU & virtual nursing.</h2>
            </div>
            <div className="lg:text-right">
              <Button data-testid="enterprise-cta-btn" onClick={() => setLoginOpen(true)}
                className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold h-14 px-8 text-base">
                Partner with Us <ArrowUpRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#041E26] pt-20 pb-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 pb-16 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Logo className="h-11" onDark showTagline />
              </div>
              <p className="text-white/50 text-sm leading-relaxed">Comprehensive care, illuminated — from hospital to home, across India.</p>
            </div>
            {[
              { h: "Portals", links: ["Patient Portal", "Provider Access", "Enterprise Admin"] },
              { h: "Company", links: ["For Individuals", "For Employers", "For Hospitals"] },
              { h: "Legal", links: ["Privacy Policy", "Terms of Service", "Compliance"] },
            ].map((col) => (
              <div key={col.h}>
                <h4 className="text-white font-semibold mb-4 text-sm">{col.h}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l}><button onClick={() => setLoginOpen(true)} className="text-white/50 hover:text-[#F59E0B] text-sm transition-colors">{l}</button></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-red-950/40 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
            <HeartPulse className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <p className="text-red-200/80 text-sm">If you are experiencing a life-threatening medical emergency, please visit the nearest hospital or call <span className="font-bold text-red-300">112</span> immediately.</p>
          </div>
          <p className="text-white/30 text-xs mt-8">© {new Date().getFullYear()} Candle Healthcare. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
